import { IonButton, IonIcon, IonInput, IonText } from '@ionic/react';
import React, { useState } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { SelectModal } from '@/components/modals';
import { DurationSummary } from '@/components/summary';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import { useToast } from '@/hooks';
import {
  PURPOSE_JOIN_SEPARATOR,
  SCHEDULE_FLOW_STEPS,
  SELECT_MODAL_OTHERS
} from '@/utils/constants';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './SelectPurpose.css';

interface SelectPurposeProps {
  presentingElement?: HTMLElement | undefined;
}

const SelectPurpose: React.FC<SelectPurposeProps> = ({ presentingElement }) => {
  const { t } = useLanguage();
  const {
    clinic,
    purpose,
    services,
    setPurpose,
    othersPurpose,
    setOthersPurpose,
    availableDates,
    loadAvailableDates,
    setCurrentStep
  } = useScheduleFlow();
  const displayToast = useToast();

  const [hasError, setHasError] = useState<boolean>(false);
  const [lastSavedPurpose, setLastSavedPurpose] = useState<string | null>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState<boolean>(false);

  const copy = {
    subHeader: t('schedule.selectPurpose.subHeader'),
    header: (
      <>
        {t('schedule.selectPurpose.header.select')}{' '}
        <IonText color="primary">
          {t('schedule.selectPurpose.header.procedures')}
        </IonText>
      </>
    ),
    message: t('schedule.selectPurpose.message'),
    buttonText: t('schedule.selectPurpose.buttonText'),
    inputLabel: t('schedule.selectPurpose.inputLabel'),
    inputPlaceholder: t('schedule.selectPurpose.inputPlaceholder'),
    modalTitle: (
      <>
        {t('schedule.selectPurpose.modalTitle.select')}{' '}
        <IonText color="primary">
          {t('schedule.selectPurpose.modalTitle.procedures')}
        </IonText>
      </>
    ),
    othersLabel: t('schedule.selectPurpose.othersLabel'),
    othersPlaceholder: t('schedule.selectPurpose.othersPlaceholder'),
    errorMessage: t('schedule.selectPurpose.errorMessage'),
    helpPrompt: t('schedule.selectPurpose.helpPrompt')
  };

  const options = services.map((option) => option.name);

  const isSelected = (value: string) =>
    purpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;

  const isPreSelected = (value: string) =>
    lastSavedPurpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;

  const onSelect = (value: string, isSelected: boolean) => {
    if (purpose) {
      if (isSelected) {
        setPurpose(
          purpose
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== value)
            .join(PURPOSE_JOIN_SEPARATOR)
        );
      } else {
        setPurpose(purpose + PURPOSE_JOIN_SEPARATOR + value);
      }
    } else {
      setPurpose(value);
    }
  };

  const onDismiss = () => {
    setIsSelectModalOpen(false);
    setLastSavedPurpose(purpose);
  };

  const onNext = async () => {
    if (purpose) {
      setHasError(false);
      if (availableDates.length > 0) {
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
        return;
      }
      await loadAvailableDates();
      return;
    }

    setHasError(true);
    displayToast({
      message: copy.errorMessage,
      duration: 5000,
      position: 'bottom',
      positionAnchor: 'nextButton'
    });
  };

  const onInput = (event: CustomEvent) => {
    const value = event.detail.value;
    setOthersPurpose(value);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  return (
    <div className="select-purpose">
      <Spacer top={32} bottom={24}>
        <h4 className="select-purpose__sub-header">{copy.subHeader}</h4>
        <h1 className="select-purpose__header">{copy.header}</h1>
        <p className="select-purpose__message">{copy.message}</p>
      </Spacer>
      <div className="select-purpose__form">
        <div className={`input ${hasError ? 'error' : ''}`}>
          <IonInput
            className="select-purpose__purpose-input"
            label={copy.inputLabel}
            labelPlacement="stacked"
            value={purpose}
            placeholder={copy.inputPlaceholder}
            onClick={() => {
              setHasError(false);
              setIsSelectModalOpen(true);
            }}
            readonly
          />
        </div>
        {purpose === SELECT_MODAL_OTHERS && (
          <div className="input">
            <IonInput
              className="select-purpose__others-input"
              label={copy.othersLabel}
              labelPlacement="stacked"
              value={othersPurpose}
              placeholder={copy.othersPlaceholder}
              onIonInput={(event: CustomEvent) => onInput(event)}
              onKeyDown={onKeyDown}
            />
          </div>
        )}
      </div>
      {purpose ? (
        <Spacer top={24}>
          <DurationSummary />
        </Spacer>
      ) : (
        <Spacer top={8}>
          <p className="select-purpose__help-prompt">
            {copy.helpPrompt}{' '}
            <IonText color="primary">
              {formatMobileNumber(clinic?.mobile_number)}
            </IonText>
          </p>
        </Spacer>
      )}
      <Spacer bottom={102} />
      <ButtonContainer>
        <IonButton
          className="select-purpose__next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>

      <SelectModal
        isOpen={isSelectModalOpen}
        presentingElement={presentingElement}
        title={copy.modalTitle}
        options={options}
        isSelected={isSelected}
        isPreSelected={isPreSelected}
        onSelect={onSelect}
        onDismiss={onDismiss}
        multiple
      />
    </div>
  );
};

export default SelectPurpose;
